import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Users, Search, X, UserPlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Teams / Slack-style "New chat" dialog with two tabs:
 *   - DM: pick one employee → start a 1-on-1
 *   - Group: name it + pick 2+ employees → creates a group channel
 */
export default function NewChatDialog({ open, onOpenChange, employees, onStartDM, onGroupCreated }) {
  const { api, user } = useAuth();
  const [tab, setTab] = useState('dm');
  const [q, setQ] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setTab('dm');
      setQ('');
      setGroupName('');
      setSelected([]);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const others = (employees || []).filter((e) => e.user_id !== user?.id);
    if (!q.trim()) return others;
    const needle = q.toLowerCase();
    return others.filter((e) =>
      (e.name || '').toLowerCase().includes(needle) ||
      (e.email || '').toLowerCase().includes(needle) ||
      (e.role_label || '').toLowerCase().includes(needle)
    );
  }, [employees, q, user?.id]);

  const toggle = (emp) => {
    setSelected((prev) => prev.find((p) => p.user_id === emp.user_id)
      ? prev.filter((p) => p.user_id !== emp.user_id)
      : [...prev, emp]
    );
  };

  const startDM = (emp) => {
    onStartDM(emp);
    onOpenChange(false);
  };

  const createGroup = async () => {
    if (!groupName.trim()) return toast.error('Give the group a name');
    if (selected.length < 1) return toast.error('Pick at least one teammate');
    setBusy(true);
    try {
      const res = await api.post('/chat/groups', {
        name: groupName.trim(),
        member_ids: selected.map((s) => s.user_id),
      });
      toast.success(`Group "${res.data.group.name}" created`);
      onGroupCreated(res.data.group);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create group');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-chat-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-500" /> Start a chat
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border rounded-full p-1 bg-slate-50 w-fit mx-auto">
          <button
            type="button"
            onClick={() => setTab('dm')}
            className={`px-4 py-1 text-sm rounded-full flex items-center gap-1 ${tab === 'dm' ? 'bg-emerald-500 text-white' : 'text-slate-600'}`}
            data-testid="new-chat-tab-dm"
          >
            <MessageSquare className="w-4 h-4" /> Direct Message
          </button>
          <button
            type="button"
            onClick={() => setTab('group')}
            className={`px-4 py-1 text-sm rounded-full flex items-center gap-1 ${tab === 'group' ? 'bg-emerald-500 text-white' : 'text-slate-600'}`}
            data-testid="new-chat-tab-group"
          >
            <Users className="w-4 h-4" /> Group Chat
          </button>
        </div>

        {tab === 'group' && (
          <Input
            placeholder="Group name (e.g. Design Team)"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            data-testid="new-group-name-input"
          />
        )}

        {tab === 'group' && selected.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selected.map((s) => (
              <Badge key={s.user_id} variant="secondary" className="flex items-center gap-1" data-testid={`selected-member-${s.user_id}`}>
                {s.name}
                <button type="button" onClick={() => toggle(s)} className="ml-1"><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
        )}

        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search teammates by name, email, or role…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="new-chat-search"
          />
        </div>

        <div className="max-h-64 overflow-y-auto border rounded-lg divide-y" data-testid="new-chat-list">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-400 p-4 text-center">No teammates match.</p>
          ) : filtered.map((emp) => {
            const isSelected = !!selected.find((s) => s.user_id === emp.user_id);
            return (
              <button
                key={emp.user_id}
                type="button"
                onClick={() => tab === 'dm' ? startDM(emp) : toggle(emp)}
                className={`w-full text-left p-3 hover:bg-slate-50 flex items-center gap-3 ${isSelected ? 'bg-emerald-50' : ''}`}
                data-testid={`new-chat-employee-${emp.user_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {(emp.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{emp.name}</p>
                  <p className="text-xs text-slate-500 truncate">{emp.role_label}</p>
                </div>
                {tab === 'group' && (
                  <input type="checkbox" checked={isSelected} readOnly className="pointer-events-none" />
                )}
              </button>
            );
          })}
        </div>

        {tab === 'group' && (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={createGroup} disabled={busy || !groupName.trim() || selected.length < 1} className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-group-submit">
              {busy ? 'Creating…' : `Create group (${selected.length + 1})`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
